import cloneDeep from "lodash/cloneDeep";

class TreeNode {
  static intId = 1;
  static keyCounter = 100;

  constructor(data) {
    this.intKey = TreeNode.keyCounter;
    TreeNode.keyCounter += 100;
    this.data = data;
    this.children = [];
    this.intId = TreeNode.intId;
    TreeNode.intId++;
  }

  clone() {
    const clonedNode = new TreeNode(cloneDeep(this.data));
    for (const child of this.children) {
      const clonedChild = child.clone();
      clonedNode.children.push(clonedChild);
    }

    return clonedNode;
  }

  addChild(childNode) {
    this.children.push(childNode);
  }
}

class Tree {
  constructor(data) {
    this.root = new TreeNode(data);
  }

  reIndex() {
    let startIndex = 1;
    this.traverseBF(node => {
      node.intId = startIndex;
      startIndex++;
    });
  }

  addNode(parentNode, data) {
    const newNode = new TreeNode(cloneDeep(data));
    parentNode.addChild(newNode);
    this.reIndex();

    return newNode;
  }

  copyNode(nodeToCopy, newParentNode) {
    if (!nodeToCopy || !newParentNode) {
      return;
    }

    const copiedNode = nodeToCopy.clone();
    newParentNode.children.push(copiedNode);
    this.reIndex();

    return copiedNode;
  }

  copyToRight(parentNode, childNode) {
    if (!parentNode || !childNode) {
      return null;
    }

    const index = parentNode.children.indexOf(childNode);
    if (index === -1) {
      return null;
    }

    const clonedNode = childNode.clone();
    parentNode.children.splice(index + 1, 0, clonedNode);
    this.reIndex();

    return clonedNode;
  }

  removeNode(parentNode, nodeToRemove) {
    if (!parentNode || !nodeToRemove) {
      return;
    }

    const indexToRemove = parentNode.children.indexOf(nodeToRemove);
    if (indexToRemove !== -1) {
      parentNode.children.splice(indexToRemove, 1);
    }
    this.reIndex();
  }

  findNode(targetData, startNode = this.root) {
    if (!startNode) {
      return null;
    }

    if (startNode.data === targetData) {
      return startNode;
    }

    for (const child of startNode.children) {
      const result = this.findNode(targetData, child);
      if (result) {
        return result;
      }
    }

    return null;
  }

  // Depth-First Traversal (Pre-order)
  traverseDF(node, callback) {
    callback(node);
    for (const child of node.children) {
      this.traverseDF(child, callback);
    }
  }

  // Breadth-First Traversal
  traverseBF(callback) {
    const queue = [this.root];
    while (queue.length > 0) {
      const currentNode = queue.shift();
      callback(currentNode);
      queue.push(...currentNode.children);
    }
  }
}

function constructTreeFromObject(obj) {
  if (!obj || !obj.root || !obj.root.data) {
    return null;
  }

  const tree = new Tree(obj.root.data);

  const rootNode = tree.root;

  function constructChildren(parentNode, childrenArray) {
    for (const childObj of childrenArray) {
      const childNode = tree.addNode(parentNode, childObj.data);

      if (childObj.children && childObj.children.length > 0) {
        constructChildren(childNode, childObj.children);
      }
    }
  }

  constructChildren(rootNode, obj.root.children);

  return tree;
}

export { Tree, TreeNode, constructTreeFromObject };
